import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixMembersV1CustomFieldSuggestion from './members-v1-custom-field-suggestion-custom-field-suggestions.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

/** A custom field that can be suggested for Wix users to add to member profiles. */
export interface CustomFieldSuggestion {
  /**
   * Custom field name.
   * @maxLength 150
   */
  name?: string | null;
  /** Privacy level of the custom field. */
  defaultPrivacy?: PrivacyWithLiterals;
  /** Type of information to provide for members. */
  fieldType?: TypeWithLiterals;
  /** Social network type. */
  socialType?: SocialTypeTypeWithLiterals;
  /** Field origin. */
  fieldOrigin?: OriginWithLiterals;
  /**
   * Custom field ID.
   * @format GUID
   */
  customFieldId?: string | null;
}

export enum Privacy {
  /** Unknown privacy. This value isn't used. */
  UNKNOWN = 'UNKNOWN',
  /** The information appears on the members' public profile pages. */
  PUBLIC = 'PUBLIC',
  /** Only the member can see this information. */
  PRIVATE = 'PRIVATE',
}

/** @enumType */
export type PrivacyWithLiterals = Privacy | 'UNKNOWN' | 'PUBLIC' | 'PRIVATE';

export enum Type {
  /** Unknown field type. This value isn't used. */
  UNKNOWN = 'UNKNOWN',
  /** A text box to write text. */
  TEXT = 'TEXT',
  /** Only a number can be entered into the field. */
  NUMBER = 'NUMBER',
  /** Only a date can be entered into the field. */
  DATE = 'DATE',
  /** Only a URL can be entered into the field. */
  URL = 'URL',
  /** Only a link from the selected social media platform can be entered into the field. */
  SOCIAL = 'SOCIAL',
}

/** @enumType */
export type TypeWithLiterals =
  | Type
  | 'UNKNOWN'
  | 'TEXT'
  | 'NUMBER'
  | 'DATE'
  | 'URL'
  | 'SOCIAL';

export enum SocialTypeType {
  /** Unknown social type. This value isn't used. */
  UNKNOWN = 'UNKNOWN',
  /** Facebook social media platform. */
  FACEBOOK = 'FACEBOOK',
  /** Instagram social media platform. */
  INSTAGRAM = 'INSTAGRAM',
  /** LinkedIn social media platform. */
  LINKEDIN = 'LINKEDIN',
  /** X (formerly Twitter) social media platform. */
  TWITTER = 'TWITTER',
  /** Youtube social media platform. */
  YOUTUBE = 'YOUTUBE',
  /** Pinterest social media platform. */
  PINTEREST = 'PINTEREST',
  /** TikTok social media platform. */
  TIKTOK = 'TIKTOK',
  /** DeviantArt social media platform. */
  DEVIANTART = 'DEVIANTART',
  /** SoundCloud social media platform. */
  SOUNDCLOUD = 'SOUNDCLOUD',
  /** Tumblr social media platform. */
  TUMBLR = 'TUMBLR',
  /** Vimeo social media platform. */
  VIMEO = 'VIMEO',
  /** VKontakte social media platform. */
  VKONTAKTE = 'VKONTAKTE',
  /** Odnoklassniki social media platform. */
  ODNOKLASSNIKI = 'ODNOKLASSNIKI',
  /** Other social media platform. */
  OTHER = 'OTHER',
}

/** @enumType */
export type SocialTypeTypeWithLiterals =
  | SocialTypeType
  | 'UNKNOWN'
  | 'FACEBOOK'
  | 'INSTAGRAM'
  | 'LINKEDIN'
  | 'TWITTER'
  | 'YOUTUBE'
  | 'PINTEREST'
  | 'TIKTOK'
  | 'DEVIANTART'
  | 'SOUNDCLOUD'
  | 'TUMBLR'
  | 'VIMEO'
  | 'VKONTAKTE'
  | 'ODNOKLASSNIKI'
  | 'OTHER';

export enum Origin {
  /** Unknown field origin. This value isn't used. */
  UNKNOWN = 'UNKNOWN',
  /** Custom field created by a Wix user. */
  CUSTOM = 'CUSTOM',
  /** Contact type of field. These are default fields that are already provided by Members Area, such as: "birth date", "position", "company". */
  CONTACT = 'CONTACT',
  /** System fields, such as: "first name", "last name", "email", "phone", "address", and  "title". Some system fields are only available from the dashboard. */
  SYSTEM = 'SYSTEM',
}

/** @enumType */
export type OriginWithLiterals =
  | Origin
  | 'UNKNOWN'
  | 'CUSTOM'
  | 'CONTACT'
  | 'SYSTEM';

export interface ListCustomFieldSuggestionsRequest {
  paging?: Paging;
  sorting?: Sorting[];
}

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
  /**
   * Origin point for geo-distance sorting on a GEO field
   * results are ordered by distance from this point (ASC = nearest first, DESC = farthest first).
   */
  origin?: AddressLocation;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface AddressLocation {
  /** Address latitude. */
  latitude?: number | null;
  /** Address longitude. */
  longitude?: number | null;
}

export interface ListCustomFieldSuggestionsResponse {
  /** Custom field suggestions. */
  suggestions?: CustomFieldSuggestion[];
  /** Metadata for the paginated results. */
  metadata?: PagingMetadata;
}

export interface PagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
}

export interface QueryCustomFieldSuggestionsRequest {
  /** Query options. See [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language) for more details. */
  query?: Query;
}

export interface Query {
  /**
   * Filter object.
   *
   * Learn more about the [filter section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).
   */
  filter?: any;
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  paging?: Paging;
  /**
   * Sort object.
   *
   * Learn more about the [sort section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-sort-section).
   */
  sorting?: Sorting[];
}

export interface QueryCustomFieldSuggestionsResponse {
  /** Custom field suggestion. */
  suggestions?: CustomFieldSuggestion[];
  /** Metadata for the paginated results. */
  metadata?: PagingMetadata;
}

/**
 * Retrieves a list of suggestions for both custom fields and existing fields available from the Wix Members Area.
 * @public
 * @permissionId MEMBERS.CUSTOM_FIELDS_SUGGESTIONS_READ
 * @applicableIdentity APP
 * @fqn com.wixpress.members.customfields.suggestions.CustomFieldSuggestions.ListCustomFieldSuggestions
 */
export async function listCustomFieldSuggestions(
  options?: ListCustomFieldSuggestionsOptions
): Promise<
  NonNullablePaths<
    ListCustomFieldSuggestionsResponse,
    | `suggestions`
    | `suggestions.${number}.defaultPrivacy`
    | `suggestions.${number}.fieldType`
    | `suggestions.${number}.socialType`
    | `suggestions.${number}.fieldOrigin`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    paging: options?.paging,
    sorting: options?.sorting,
  });

  const reqOpts =
    ambassadorWixMembersV1CustomFieldSuggestion.listCustomFieldSuggestions(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          paging: '$[0].paging',
          sorting: '$[0].sorting',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListCustomFieldSuggestionsOptions {
  paging?: Paging;
  sorting?: Sorting[];
}

/**
 * Retrieves a list of up to 100 custom field suggestions, given the provided filters, fieldsets, sorting and paging, and returns a [`SuggestionsQueryBuilder`](https://dev.wix.com/docs/sdk/backend-modules/members/custom-field-suggestions/suggestions-query-builder/find) object.
 *
 * The returned object contains the query definition which is typically used to run the query using the [`find()`](https://dev.wix.com/docs/sdk/backend-modules/members/custom-field-suggestions/suggestions-query-builder/find) method.
 *
 * You can refine the query by chaining `SuggestionsQueryBuilder` methods onto the query. `SuggestionsQueryBuilder` methods enable you to sort, filter, and control the results that `queryCustomFieldSuggestions()` returns. The methods that are chained to `queryCustomFieldSuggestions()` are applied in the order they are called.
 *
 * `queryCustomFieldSuggestions()` runs with the following `SuggestionsQueryBuilder` defaults that you can override:
 * - [`skip`: `0`](https://dev.wix.com/docs/sdk/backend-modules/members/custom-field-suggestions/suggestions-query-builder/skip)
 * - [`limit`: `50`](https://dev.wix.com/docs/sdk/backend-modules/members/custom-field-suggestions/suggestions-query-builder/limit)
 *
 *
 * @public
 * @permissionId MEMBERS.CUSTOM_FIELDS_SUGGESTIONS_READ
 * @applicableIdentity APP
 * @fqn com.wixpress.members.customfields.suggestions.CustomFieldSuggestions.QueryCustomFieldSuggestions
 */
export function queryCustomFieldSuggestions(): SuggestionsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    CustomFieldSuggestion,
    'OFFSET',
    QueryCustomFieldSuggestionsRequest,
    QueryCustomFieldSuggestionsResponse
  >({
    func: async (payload: QueryCustomFieldSuggestionsRequest) => {
      const reqOpts =
        ambassadorWixMembersV1CustomFieldSuggestion.queryCustomFieldSuggestions(
          payload
        );

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (
      query: QueryCustomFieldSuggestionsRequest['query']
    ) => {
      const args = [query, {}] as [
        QueryCustomFieldSuggestionsRequest['query'],
        {}
      ];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({
      data,
    }: HttpResponse<QueryCustomFieldSuggestionsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [])
      );

      return {
        items: transformedData?.suggestions,
        pagingMetadata: transformedData?.metadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'OFFSET',
    transformationPaths: {},
  });
}

interface QueryOffsetResult {
  currentPage: number | undefined;
  totalPages: number | undefined;
  totalCount: number | undefined;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface SuggestionsQueryResult extends QueryOffsetResult {
  items: CustomFieldSuggestion[];
  query: SuggestionsQueryBuilder;
  next: () => Promise<SuggestionsQueryResult>;
  prev: () => Promise<SuggestionsQueryResult>;
}

export interface SuggestionsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (
    propertyName: 'name' | 'fieldType',
    value: any
  ) => SuggestionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (
    propertyName: 'name' | 'fieldType',
    value: any
  ) => SuggestionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ge: (propertyName: 'name', value: any) => SuggestionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  gt: (propertyName: 'name', value: any) => SuggestionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  le: (propertyName: 'name', value: any) => SuggestionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  lt: (propertyName: 'name', value: any) => SuggestionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   */
  startsWith: (propertyName: 'name', value: string) => SuggestionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasSome: (
    propertyName: 'name' | 'fieldType',
    value: any[]
  ) => SuggestionsQueryBuilder;
  in: (
    propertyName: 'name' | 'fieldType',
    value: any
  ) => SuggestionsQueryBuilder;
  exists: (
    propertyName: 'name' | 'fieldType',
    value: boolean
  ) => SuggestionsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  ascending: (
    ...propertyNames: Array<'name' | 'fieldType'>
  ) => SuggestionsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  descending: (
    ...propertyNames: Array<'name' | 'fieldType'>
  ) => SuggestionsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => SuggestionsQueryBuilder;
  /** @param skip - Number of items to skip in the query results before returning the results. */
  skip: (skip: number) => SuggestionsQueryBuilder;
  find: () => Promise<SuggestionsQueryResult>;
}

/**
 * @hidden
 * @fqn com.wixpress.members.customfields.suggestions.CustomFieldSuggestions.QueryCustomFieldSuggestions
 * @requiredField query
 */
export async function typedQueryCustomFieldSuggestions(
  query: CustomFieldSuggestionQuery
): Promise<
  NonNullablePaths<
    QueryCustomFieldSuggestionsResponse,
    | `suggestions`
    | `suggestions.${number}.defaultPrivacy`
    | `suggestions.${number}.fieldType`
    | `suggestions.${number}.socialType`
    | `suggestions.${number}.fieldOrigin`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts =
    ambassadorWixMembersV1CustomFieldSuggestion.queryCustomFieldSuggestions(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CustomFieldSuggestionQuerySpec extends QuerySpec {
  paging: 'offset';
  wql: [
    {
      fields: ['fieldType', 'name'];
      operators: '*';
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  CustomFieldSuggestion,
  CustomFieldSuggestionQuerySpec
>;
export type CustomFieldSuggestionQuery = {
  /** 
  Filter object.

  Learn more about the [filter section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).  
  */
  filter?: CommonQueryWithEntityContext['filter'];
  /** 
  Cursor paging options.

  Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).  
  */
  paging?: {
    /** 
  Number of items to load.  
  */
    limit?: NonNullable<CommonQueryWithEntityContext['paging']>['limit'] | null;
    /** 
  Number of items to skip in the current sort order.  
  */
    offset?:
      | NonNullable<CommonQueryWithEntityContext['paging']>['offset']
      | null;
  };
};

export const utils = {
  query: {
    ...createQueryUtils<
      CustomFieldSuggestion,
      CustomFieldSuggestionQuerySpec,
      CustomFieldSuggestionQuery
    >(),
  },
};
